import './App.css';
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardHeader from './dashboardheader.js'
import VerticalNavigation from './VerticalNavigation.js'
import AddClient from './forms/AddClient.js';
import CheckIn from './forms/CheckIn.js';
import bhcalc from './bhcalc.js'
import bhs from './bhs.js'

function DeleteClientModal({ client, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  const handleDelete = async () => {
    setDeleting(true);
    setError(null);
    try {
      await bhcalc.delete(`/partner-client/${client.id}`);
      onDeleted(client.id);
      onClose();
    } catch {
      setError('Failed to delete client. Please try again.');
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-900">Delete Client</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl font-bold leading-none">&times;</button>
          </div>
          <p className="text-sm text-left text-gray-700 mb-4">
            Are you sure you want to delete <strong>{client.firstName} {client.lastName}</strong>? This action cannot be undone.
          </p>
          {error && <p className="text-sm text-red-600 mb-4">{error}</p>}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              disabled={deleting}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleting}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors duration-200 disabled:opacity-50"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Clients() {
  const navigate = useNavigate();
  const userRole = bhs(state => state.userRole);
  const isCoach = userRole === 'coach';
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clientToManage, setClientToManage] = useState(null);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [clientToCheckIn, setClientToCheckIn] = useState(null);

  const fetchClients = useCallback(() => {
    setLoading(true);
    bhcalc.get('/partner-client')
      .then(res => setClients(res.data))
      .catch(() => setError('Failed to load clients.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const handleClientDeleted = (id) => {
    setClients(prev => prev.filter(c => c.id !== id));
  };

  const handleClientSaved = () => {
    setClientToManage(null);
    fetchClients();
  };

  return (
    <div className="App min-h-screen">
      {clientToManage && (
        <AddClient client={clientToManage} onSaved={handleClientSaved} />
      )}

      {clientToCheckIn && (
        <CheckIn client={clientToCheckIn} onClose={() => setClientToCheckIn(null)} />
      )}

      {clientToDelete && (
        <DeleteClientModal
          client={clientToDelete}
          onClose={() => setClientToDelete(null)}
          onDeleted={handleClientDeleted}
        />
      )}

      <div className="dashboard-wrapper flex flex-col min-h-screen">
        <div className="dashboard-header flex w-full max-h-24 h-14">
          <DashboardHeader />
        </div>
        <div className="w-full flex flex-wrap min-h-screen">
          <div className="dashboard-left-sidebar flex-none w-48 relative">
            <VerticalNavigation />
          </div>
          <div className="dashboard-right-main border-1 flex-auto p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-left">All Clients</h2>
              {!isCoach && (
                <button
                  onClick={() => navigate('/clients/add')}
                  className="px-3 py-1 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center space-x-2"
                >
                  <span className="text-xl">+</span>
                  <span>Add Client</span>
                </button>
              )}
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Location</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Coach</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Program</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Created</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 text-left">
                  {loading ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-4 text-center text-sm text-gray-500">Loading clients...</td>
                    </tr>
                  ) : error ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-4 text-center text-sm text-red-600">{error}</td>
                    </tr>
                  ) : clients.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-4 text-center text-sm text-gray-500">No clients found. Add your first client above.</td>
                    </tr>
                  ) : (
                    clients.map(client => (
                      <tr key={client.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{client.firstName} {client.lastName}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {!client.checkIns?.length ? (
                            <span>-</span>
                          ) : client.checkIns.some(ci => !ci.reviewed) ? (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Review Requested</span>
                          ) : (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">Reviewed</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{client.location?.name || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{client.coach ? `${client.coach.firstName} ${client.coach.lastName}` : '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{client.locationPrograms?.[0]?.programName || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{new Date(client.createdAt).toLocaleDateString()}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          {isCoach ? (
                            <button onClick={() => setClientToCheckIn(client)} className="text-green-600 hover:text-green-900">Check In</button>
                          ) : (
                            <>
                              <button onClick={() => setClientToManage(client)} className="text-blue-600 hover:text-blue-900 mr-3">Manage</button>
                              <button onClick={() => setClientToDelete(client)} className="text-red-600 hover:text-red-900">Delete</button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Clients;
